// aifab - Servers page
import { useState } from 'react';
import { useSubmit } from '../hooks/useSubmit';
import { useForm } from '../hooks/useForm';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../hooks/apiFetch';
import { Spinner, StatusBadge, StatusDot, EmptyState, Modal, ModalBody, ModalFooter, Err } from '../components/common';

export default function ServersPage() {
  const { data, loading, reload } = useApi('/servers');
  const [show,     setShow]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form,     setForm]    = useState({ server_name:'', host:'', port:22, ssh_user:'ubuntu', ssh_private_key:'', max_parallel_jobs:1, cpu_cores_per_job:4, remote_base_dir:'~/aifab' });
  const [saving,   setSaving]  = useState(false);
  const [testing,  setTesting] = useState({});
  const [testResult, setTestResult] = useState({});
  const [err,      setErr]     = useState(null);
  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const save = async () => {
    if (!form.server_name.trim()) { setErr('Server name required'); return; }
    if (!form.host.trim())        { setErr('Host / IP required'); return; }
    if (!form.ssh_private_key.trim()) { setErr('SSH private key required'); return; }
    setSaving(true); setErr(null);
    try {
      await apiFetch('/servers', { method:'POST', body:JSON.stringify({ ...form, port:+form.port }) });
      reload(); setShow(false);
      setForm({ server_name:'', host:'', port:22, ssh_user:'ubuntu', ssh_private_key:'', max_parallel_jobs:1, cpu_cores_per_job:4, remote_base_dir:'~/aifab' });
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const prepare = async (id) => {
    try {
      await apiFetch(`/servers/${id}/prepare`, { method:'POST' });
      reload();
      const t = setInterval(async () => {
        const list = await apiFetch('/servers');
        const srv = list.find(x => x.id === id);
        if (srv && srv.provision_status !== 'PREPARING') { clearInterval(t); reload(); }
      }, 5000);
    } catch (e) { setErr(e.message); }
  };

  const test = async (id) => {
    setTesting(t => ({...t,[id]:true}));
    setTestResult(r => ({...r,[id]:null}));
    try {
      const r = await apiFetch(`/servers/${id}/test`, { method:'POST' });
      setTestResult(t => ({...t,[id]:{ ok:true, msg:r.message || 'Connection successful' }}));
      reload();
    } catch(e) {
      setTestResult(t => ({...t,[id]:{ ok:false, msg:e.message }}));
    }
    finally { setTesting(t => ({...t,[id]:false})); }
  };

  return (
    <div className="page">
      <div className="page-hd">
        <div>
          <div className="page-sub">Execution servers where training jobs and inference run via SSH</div>
        </div>
        <button className="btn btn-primary btn-sm" data-tour="servers-add" onClick={() => setShow(true)}>+ Register Server</button>
      </div>

      {loading ? (
        <div className="flex" style={{ justifyContent:'center', padding:48 }}><Spinner size={28} /></div>
      ) : (data||[]).length === 0 ? (
        <EmptyState icon="▲" title="No servers registered"
          subtitle="Register an SSH server to start running training jobs"
          action={<button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>+ Register Server</button>} />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))', gap:14 }}>
          {(data||[]).map(s => (
            <div key={s.id} className="card">
              <div className="card-hd">
                <div className="flex gap8">
                  <StatusDot code={s.status_code} />
                  <div>
                    <div style={{ fontWeight:700, fontSize:13 }}>{s.server_name}</div>
                    <div className="mono xs c-3">{s.host}:{s.port}</div>
                  </div>
                </div>
                <StatusBadge code={s.status_code} />
              </div>

              <div style={{ padding:'12px 20px' }}>
                {/* Specs */}
                {s.cpu_count && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:12 }}>
                    {[['CPU', `${s.cpu_count} cores`], ['RAM', `${s.ram_gb} GB`], ['Disk', `${s.disk_gb} GB`]].map(([l,v]) => (
                      <div key={l} style={{ textAlign:'center', background:'#E4EAF4', borderRadius:'4px', padding:'8px 4px' }}>
                        <div className="xs c-3" style={{ marginBottom:2, fontWeight:600 }}>{l}</div>
                        <div className="mono sm bold">{v}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
                  <div style={{ background:'#E4EAF4', borderRadius:'4px', padding:'7px 10px' }}>
                    <div className="xs c-3" style={{ marginBottom:2 }}>Max parallel jobs</div>
                    <div className="mono sm bold">{s.max_parallel_jobs}</div>
                  </div>
                  <div style={{ background:'#E4EAF4', borderRadius:'4px', padding:'7px 10px' }}>
                    <div className="xs c-3" style={{ marginBottom:2 }}>Cores per job</div>
                    <div className="mono sm bold">{s.cpu_cores_per_job}</div>
                  </div>
                </div>

                {s.os_info && <div className="xs c-3 mb8">{s.os_info}</div>}
                {s.provision_status === 'FAILED' && s.provision_error && (
                  <div className="alert alert-err" style={{ marginBottom:8, padding:'6px 10px', fontSize:11 }}>
                    {s.provision_error}
                  </div>
                )}

                {/* Test result */}
                {testResult[s.id] && (
                  <div className={`alert ${testResult[s.id].ok ? 'alert-ok' : 'alert-err'}`} style={{ marginBottom:8, padding:'6px 10px' }}>
                    {testResult[s.id].msg}
                  </div>
                )}

                <div className="flex-btw" style={{ marginTop:4 }}>
                  <span className="xs c-3">
                    {s.last_tested_at ? `Tested ${s.last_tested_at.slice(0,16)}` : 'Never tested'}
                  </span>
                  <div className="flex gap6">
                    {s.provision_status === 'READY' ? (
                      <span className="badge b-green" title={`Prepared at ${s.remote_base_dir}`}>PREPARED</span>
                    ) : s.provision_status === 'PREPARING' ? (
                      <span className="badge b-amber"><Spinner size={9}/> PREPARING</span>
                    ) : (
                      <button className="btn btn-primary btn-sm" title={s.provision_error || 'Run automated setup on this server'}
                        onClick={e => { e.stopPropagation(); prepare(s.id); }}>
                        {s.provision_status === 'FAILED' ? 'Retry Prepare' : 'Prepare Server'}
                      </button>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => test(s.id)} disabled={testing[s.id]}>
                      {testing[s.id] ? <><Spinner size={10} /> Testing…</> : <span data-tour="servers-test">⚡ Test Connection</span>}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); setEditing(s); }}>
                      ✎ Edit
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ color:'var(--red)' }}
                      onClick={e => { e.stopPropagation(); setDeleting(s); }}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {show && (
        <Modal title="Register Server" subtitle="SSH credentials are stored in Vault - never in the database" onClose={() => setShow(false)}>
          <ModalBody>
            <Err msg={err} />
            <div className="form-row form-row-2">
              <div className="form-grp">
                <label className="form-lbl">Display Name *</label>
                <input className="form-inp" placeholder="e.g. GPU Server 1" value={form.server_name}
                  onChange={e => set('server_name', e.target.value)} />
              </div>
              <div className="form-grp">
                <label className="form-lbl">SSH User</label>
                <input className="form-inp" value={form.ssh_user}
                  onChange={e => set('ssh_user', e.target.value)} />
              </div>
            </div>
            <div className="form-grp">
              <label className="form-lbl">Fabric Directory on Server</label>
              <input className="form-inp mono" value={form.remote_base_dir}
                onChange={e => set('remote_base_dir', e.target.value)} />
              <div className="form-hint">Where Fabric keeps datasets, jobs and its Python environment. The default ~/aifab needs no admin rights - after registering, click Prepare Server and Fabric sets everything up automatically.</div>
            </div>
            <div className="form-row form-row-2">
              <div className="form-grp">
                <label className="form-lbl">Host / IP *</label>
                <input className="form-inp" placeholder="192.168.1.10 or server.domain.com"
                  value={form.host} onChange={e => set('host', e.target.value)} />
              </div>
              <div className="form-grp">
                <label className="form-lbl">Port</label>
                <input className="form-inp" type="number" value={form.port}
                  onChange={e => set('port', +e.target.value)} />
              </div>
            </div>
            <div className="form-grp">
              <label className="form-lbl">SSH Private Key (PEM) *</label>
              <textarea className="form-inp code-area" style={{ minHeight:140 }}
                placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----'}
                value={form.ssh_private_key} onChange={e => set('ssh_private_key', e.target.value)} />
              <div className="form-hint">
                Key is stored in HashiCorp Vault and never written to disk or database.
                The aifab server uses this key only to establish SSH connections.
              </div>
            </div>
            <div className="form-row form-row-2">
              <div className="form-grp">
                <label className="form-lbl">Max parallel jobs</label>
                <input className="form-inp" type="number" min="1" value={form.max_parallel_jobs}
                  onChange={e => set('max_parallel_jobs', +e.target.value)} />
                <div className="form-hint">How many training jobs this server runs simultaneously.</div>
              </div>
              <div className="form-grp">
                <label className="form-lbl">CPU cores per job</label>
                <input className="form-inp" type="number" min="1" value={form.cpu_cores_per_job}
                  onChange={e => set('cpu_cores_per_job', +e.target.value)} />
                <div className="form-hint">Cores allocated per training job. Tip: cores ÷ parallel jobs.</div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-ghost btn-sm" onClick={() => setShow(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? <Spinner size={12} /> : 'Register & Store Key'}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {/* ── Edit Server Modal ── */}
      {editing && (
        <EditServerModal
          server={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); reload(); }}
        />
      )}

      {/* ── Delete Confirm ── */}
      {deleting && (
        <DeleteServerModal
          server={deleting}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); reload(); }}
        />
      )}
    </div>
  );
}

function EditServerModal({ server, onClose, onDone }) {
  const { run, saving, err } = useSubmit();
  const { form, set } = useForm({
    server_name:       server.server_name || '',
    host:              server.host        || '',
    port:              server.port        || 22,
    ssh_user:          server.ssh_user    || '',
    ssh_private_key:   '',
    max_parallel_jobs: server.max_parallel_jobs || 1,
    cpu_cores_per_job: server.cpu_cores_per_job || 4,
  });

  const save = () => run(async () => {
    if (!form.server_name.trim()) throw new Error('Display name is required');
    if (!form.host.trim())        throw new Error('Host / IP is required');
    const body = {
      server_name:       form.server_name,
      host:              form.host,
      port:              +form.port,
      ssh_user:          form.ssh_user,
      max_parallel_jobs: +form.max_parallel_jobs,
      cpu_cores_per_job: +form.cpu_cores_per_job,
    };
    if (form.ssh_private_key.trim()) body.ssh_private_key = form.ssh_private_key;
    await apiFetch(`/servers/${server.id}`, { method:'PATCH', body:JSON.stringify(body) });
    onDone();
  });

  return (
    <Modal title="Edit Server" subtitle={server.server_name} onClose={onClose}>
      <ModalBody>
        <Err msg={err} />
        <div className="form-row form-row-2">
          <div className="form-grp">
            <label className="form-lbl">Display Name *</label>
            <input className="form-inp" value={form.server_name} onChange={e => set('server_name', e.target.value)} />
          </div>
          <div className="form-grp">
            <label className="form-lbl">SSH User</label>
            <input className="form-inp" value={form.ssh_user} onChange={e => set('ssh_user', e.target.value)} />
          </div>
        </div>
        <div className="form-row form-row-2">
          <div className="form-grp">
            <label className="form-lbl">Host / IP *</label>
            <input className="form-inp" value={form.host} onChange={e => set('host', e.target.value)} />
          </div>
          <div className="form-grp">
            <label className="form-lbl">Port</label>
            <input className="form-inp" type="number" value={form.port} onChange={e => set('port', e.target.value)} />
          </div>
        </div>
        <div className="form-grp">
          <label className="form-lbl">New SSH Private Key (PEM)</label>
          <textarea className="form-inp code-area" style={{ minHeight:100 }}
            placeholder="Leave blank to keep the existing key"
            value={form.ssh_private_key} onChange={e => set('ssh_private_key', e.target.value)} />
          <div className="form-hint">Only fill this if you want to replace the stored SSH key.</div>
        </div>
        <div className="form-row form-row-2">
          <div className="form-grp">
            <label className="form-lbl">Max parallel jobs</label>
            <input className="form-inp" type="number" min="1" value={form.max_parallel_jobs}
              onChange={e => set('max_parallel_jobs', +e.target.value)} />
          </div>
          <div className="form-grp">
            <label className="form-lbl">CPU cores per job</label>
            <input className="form-inp" type="number" min="1" value={form.cpu_cores_per_job}
              onChange={e => set('cpu_cores_per_job', +e.target.value)} />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <Spinner size={12} /> : 'Save Changes'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function DeleteServerModal({ server, onClose, onDone }) {
  const { run, saving, err } = useSubmit();

  const confirm = () => run(async () => {
    await apiFetch(`/servers/${server.id}`, { method:'DELETE' });
    onDone();
  });

  return (
    <Modal title="Delete Server" onClose={onClose}>
      <ModalBody>
        <Err msg={err} />
        <p style={{ fontSize:13, color:'#334155', marginBottom:12 }}>
          Are you sure you want to delete <strong style={{ color:'#0F172A' }}>{server.server_name}</strong>?
        </p>
        <p style={{ fontSize:12, color:'#64748B' }}>
          The SSH key stored in Vault will also be removed. This cannot be undone.
          Servers with active training jobs cannot be deleted.
        </p>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-sm" style={{ background:'var(--red)', color:'#0F172A' }}
          onClick={confirm} disabled={saving}>
          {saving ? <Spinner size={12} /> : 'Delete Server'}
        </button>
      </ModalFooter>
    </Modal>
  );
}